import { RefreshCw, RotateCcw, Save } from 'lucide-react';
import { useState, useEffect } from 'react';
import Button from '../../../components/Button';
import { ButtonWithConfirmation } from '../../../components/ButtonWithConfirmation';
import ErrorDisplay from '../../../components/ErrorDisplay';
import { Toggle } from '../../../components/Toggle';
import { OnboardingProgress, type ObsessLessUser } from '../../../types/commonTypes';
import type { OcdCycleStepsData } from '../services/ocdCycleStepsTypes';
import { userProfileService } from '../services/userProfileService';
import { DeviceInfoCard } from './DeviceInfoCard';

// Format date helper
const formatDate = (date: Date | undefined): string => {
    if (!date) return 'N/A';
    return date.toLocaleString();
};

interface UserProfileTabProps {
    currentUser: ObsessLessUser;
    onUserUpdate: () => Promise<void>;
}

export const UserProfileTab = ({ currentUser, onUserUpdate }: UserProfileTabProps) => {
    const [selectedOnboardingProgress, setSelectedOnboardingProgress] = useState<string>('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [ocdCycleSteps, setOcdCycleSteps] = useState<OcdCycleStepsData | null>(null);
    const [isLoadingCycleSteps, setIsLoadingCycleSteps] = useState(false);
    const [isRegeneratingCycleSteps, setIsRegeneratingCycleSteps] = useState(false);
    const [cycleStepsError, setCycleStepsError] = useState<string | null>(null);
    const [cycleStepsSuccessMessage, setCycleStepsSuccessMessage] = useState<string | null>(null);

    const [sttSessionsThisWeek, setSttSessionsThisWeek] = useState<number>(0);
    const [isSavingStt, setIsSavingStt] = useState(false);
    const [sttError, setSttError] = useState<string | null>(null);
    const [sttSuccessMessage, setSttSuccessMessage] = useState<string | null>(null);

    const [voiceModeEnabled, setVoiceModeEnabled] = useState(false);
    const [isSavingVoiceMode, setIsSavingVoiceMode] = useState(false);
    const [featureFlagsError, setFeatureFlagsError] = useState<string | null>(null);

    const [voiceConversationsThisWeek, setVoiceConversationsThisWeek] = useState<number>(0);
    const [voiceMinutesThisWeek, setVoiceMinutesThisWeek] = useState<number>(0);
    const [isSavingVoice, setIsSavingVoice] = useState(false);
    const [voiceError, setVoiceError] = useState<string | null>(null);
    const [voiceSuccessMessage, setVoiceSuccessMessage] = useState<string | null>(null);

    // undefined while loading, null when the Auth user has no name.
    const [authDisplayName, setAuthDisplayName] = useState<string | null | undefined>(undefined);
    const [authDisplayNameError, setAuthDisplayNameError] = useState<string | null>(null);

    useEffect(() => {
        if (currentUser && 'onboardingProgress' in currentUser) {
            setSelectedOnboardingProgress(currentUser.onboardingProgress);
        } else {
            setSelectedOnboardingProgress('');
        }

        setVoiceModeEnabled(currentUser.featureFlags?.voiceMode ?? false);
        setSttSessionsThisWeek(currentUser.sttUsage?.sessionsThisWeek ?? 0);
        setVoiceConversationsThisWeek(currentUser.voiceUsage?.conversationsThisWeek ?? 0);
        setVoiceMinutesThisWeek(currentUser.voiceUsage?.minutesThisWeek ?? 0);

        setError(null);
        setSuccessMessage(null);
        setFeatureFlagsError(null);
        setSttError(null);
        setSttSuccessMessage(null);
        setVoiceError(null);
        setVoiceSuccessMessage(null);
    }, [currentUser]);

    useEffect(() => {
        const loadOcdCycleSteps = async () => {
            if (!currentUser?.uid) {
                return;
            }

            setIsLoadingCycleSteps(true);
            setCycleStepsError(null);
            setCycleStepsSuccessMessage(null);

            try {
                const steps = await userProfileService.getOcdCycleSteps(currentUser.uid);
                setOcdCycleSteps(steps);
            } catch (err) {
                setCycleStepsError(err instanceof Error ? err.message : 'Failed to load OCD cycle steps');
                setOcdCycleSteps(null);
            } finally {
                setIsLoadingCycleSteps(false);
            }
        };

        loadOcdCycleSteps();
    }, [currentUser?.uid]);

    useEffect(() => {
        if (!currentUser?.uid) {
            return;
        }

        let isCurrent = true;
        setAuthDisplayName(undefined);
        setAuthDisplayNameError(null);

        userProfileService.getAuthDisplayName(currentUser.uid)
            .then((name) => {
                if (isCurrent) setAuthDisplayName(name);
            })
            .catch((err: unknown) => {
                if (!isCurrent) return;
                setAuthDisplayName(null);
                setAuthDisplayNameError(err instanceof Error ? err.message : 'Failed to load the Auth name');
            });

        return () => {
            isCurrent = false;
        };
    }, [currentUser?.uid]);

    const handleRegenerateCycleSteps = async () => {
        if (!currentUser?.uid) {
            return;
        }

        setIsRegeneratingCycleSteps(true);
        setCycleStepsError(null);
        setCycleStepsSuccessMessage(null);

        try {
            await userProfileService.regenerateOcdCycleSteps(currentUser.uid);
            const updatedSteps = await userProfileService.getOcdCycleSteps(currentUser.uid);
            setOcdCycleSteps(updatedSteps);
            setCycleStepsSuccessMessage('OCD cycle steps regenerated successfully!');
        } catch (err) {
            setCycleStepsError(err instanceof Error ? err.message : 'Failed to regenerate OCD cycle steps');
        } finally {
            setIsRegeneratingCycleSteps(false);
        }
    };

    const handleSave = async () => {
        if (!currentUser || !selectedOnboardingProgress) {
            setError('Please select an onboarding status');
            return;
        }

        setIsSaving(true);
        setError(null);
        setSuccessMessage(null);

        try {
            await userProfileService.updateOnboardingStatus(
                currentUser.uid,
                selectedOnboardingProgress
            );
            setSuccessMessage('Onboarding status updated successfully!');
            await onUserUpdate();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to update onboarding status');
        } finally {
            setIsSaving(false);
        }
    };

    /**
     * Saves on flip rather than behind a Save button — one boolean, and the admin can see
     * the result immediately in the app. Optimistic, reverted if the write fails; the
     * refetch afterwards is what reconciles the switch with what actually landed.
     */
    const handleToggleVoiceMode = async (enabled: boolean) => {
        if (!currentUser || isSavingVoiceMode) return;

        setIsSavingVoiceMode(true);
        setFeatureFlagsError(null);
        setVoiceModeEnabled(enabled);

        try {
            await userProfileService.updateVoiceModeEnabled(currentUser.uid, enabled);
            await onUserUpdate();
        } catch (err) {
            setVoiceModeEnabled(!enabled);
            setFeatureFlagsError(err instanceof Error ? err.message : 'Failed to update voice mode');
        } finally {
            setIsSavingVoiceMode(false);
        }
    };

    const handleSaveSttUsage = async () => {
        if (!currentUser) return;

        setIsSavingStt(true);
        setSttError(null);
        setSttSuccessMessage(null);

        try {
            await userProfileService.updateSttUsage(currentUser.uid, sttSessionsThisWeek);
            setSttSuccessMessage('STT usage updated successfully!');
            await onUserUpdate();
        } catch (err) {
            setSttError(err instanceof Error ? err.message : 'Failed to update STT usage');
        } finally {
            setIsSavingStt(false);
        }
    };

    const handleResetSttSessions = async () => {
        if (!currentUser) return;

        setIsSavingStt(true);
        setSttError(null);
        setSttSuccessMessage(null);

        try {
            await userProfileService.updateSttUsage(currentUser.uid, 0);
            setSttSessionsThisWeek(0);
            setSttSuccessMessage('STT sessions reset.');
            await onUserUpdate();
        } catch (err) {
            setSttError(err instanceof Error ? err.message : 'Failed to reset STT sessions');
        } finally {
            setIsSavingStt(false);
        }
    };

    /** Resets one count and keeps the stored value of the other, not its unsaved edit. */
    const handleResetVoiceUsage = async (field: 'conversations' | 'minutes') => {
        if (!currentUser) return;

        const conversations = field === 'conversations' ? 0 : currentUser.voiceUsage?.conversationsThisWeek ?? 0;
        const minutes = field === 'minutes' ? 0 : currentUser.voiceUsage?.minutesThisWeek ?? 0;

        setIsSavingVoice(true);
        setVoiceError(null);
        setVoiceSuccessMessage(null);

        try {
            await userProfileService.updateVoiceUsage(currentUser.uid, conversations, minutes);
            if (field === 'conversations') {
                setVoiceConversationsThisWeek(0);
            } else {
                setVoiceMinutesThisWeek(0);
            }
            setVoiceSuccessMessage(`Voice ${field} reset.`);
            await onUserUpdate();
        } catch (err) {
            setVoiceError(err instanceof Error ? err.message : `Failed to reset voice ${field}`);
        } finally {
            setIsSavingVoice(false);
        }
    };

    const handleSaveVoiceUsage = async () => {
        if (!currentUser) return;

        setIsSavingVoice(true);
        setVoiceError(null);
        setVoiceSuccessMessage(null);

        try {
            await userProfileService.updateVoiceUsage(
                currentUser.uid,
                voiceConversationsThisWeek,
                voiceMinutesThisWeek
            );
            setVoiceSuccessMessage('Voice usage updated successfully!');
            await onUserUpdate();
        } catch (err) {
            setVoiceError(err instanceof Error ? err.message : 'Failed to update voice usage');
        } finally {
            setIsSavingVoice(false);
        }
    };

    const onboardingProgressOptions = Object.values(OnboardingProgress);

    return (
        <div className="flex flex-col gap-4">
            {/* User Info Section */}
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">User Information</h2>
                <div className="text-sm text-gray-800 space-y-2">
                    <p className="mb-2">
                        User ID: <span className="font-bold break-all">{currentUser?.uid}</span>
                    </p>
                    <p className="mb-2">
                        User Name (profile): <span className="font-bold">{currentUser?.displayName}</span>
                    </p>
                    <p className="mb-2">
                        User Name (Firebase Auth):{' '}
                        {authDisplayNameError ? (
                            <span className="text-red-600">{authDisplayNameError}</span>
                        ) : authDisplayName === undefined ? (
                            <span className="text-gray-500">Loading...</span>
                        ) : (
                            <span className="font-bold">{authDisplayName ?? <span className="font-normal text-gray-500">None</span>}</span>
                        )}
                    </p>
                    <p className="mb-2">
                        User Email: <span className="font-bold break-all">{currentUser?.email}</span>
                    </p>

                    {('createdAt' in currentUser) && currentUser.createdAt && (
                        <p className="mb-2">
                            Created At: <span className="font-bold">{formatDate(currentUser.createdAt)}</span>
                        </p>
                    )}

                    {('hasFinishedOnboarding' in currentUser) && (
                        <p className="mb-2">
                            Has Finished Onboarding:
                            <span className={`font-bold ml-2 ${currentUser.hasFinishedOnboarding ? 'text-green-600' : 'text-red-600'}`}>
                                {currentUser.hasFinishedOnboarding ? 'Yes' : 'No'}
                            </span>
                        </p>
                    )}

                    {currentUser.referralCode && (
                        <p className="mb-2">
                            Referral Code: <span className="font-bold">{currentUser.referralCode}</span>
                        </p>
                    )}
                </div>
            </div>

            <DeviceInfoCard deviceInfo={currentUser.deviceInfo} timezone={currentUser.timezone} />

            {/* Quest Journey Section */}
            {currentUser.questJourney && (
                <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Quest Journey</h3>
                    <div className="text-sm text-gray-800 space-y-2">
                        <p className="mb-1">
                            Current Day: <span className="font-bold">{currentUser.questJourney.currentDay}</span>
                        </p>
                        <p className="mb-1">
                            Is Current Day Completed:
                            <span className={`font-bold ml-2 ${currentUser.questJourney.isCurrentDayCompleted ? 'text-green-600' : 'text-red-600'}`}>
                                {currentUser.questJourney.isCurrentDayCompleted ? 'Yes' : 'No'}
                            </span>
                        </p>
                        {currentUser.questJourney.journeyDayCompletedAt && (
                            <p className="mb-1">
                                Journey Day Completed At: <span className="font-bold">{formatDate(currentUser.questJourney.journeyDayCompletedAt)}</span>
                            </p>
                        )}
                        {currentUser.questJourney.journeyDayStartedAt && (
                            <p className="mb-1">
                                Journey Day Started At: <span className="font-bold">{formatDate(currentUser.questJourney.journeyDayStartedAt)}</span>
                            </p>
                        )}
                        {currentUser.questJourney.createdAt && (
                            <p className="mb-1">
                                Created At: <span className="font-bold">{formatDate(currentUser.questJourney.createdAt)}</span>
                            </p>
                        )}
                        {currentUser.questJourney.updatedAt && (
                            <p className="mb-1">
                                Updated At: <span className="font-bold">{formatDate(currentUser.questJourney.updatedAt)}</span>
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Onboarding Status Section */}
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Onboarding Status</h2>

                {error && <ErrorDisplay message={error} />}
                {successMessage && (
                    <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
                        {successMessage}
                    </div>
                )}

                <div className="mb-4">
                    <label htmlFor="onboarding-progress" className="block text-sm font-medium text-gray-700 mb-2">
                        Onboarding Progress
                    </label>
                    <select
                        id="onboarding-progress"
                        value={selectedOnboardingProgress}
                        onChange={(e) => setSelectedOnboardingProgress(e.target.value)}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                        <option value="">Select onboarding status...</option>
                        {onboardingProgressOptions.map((option) => (
                            <option key={option} value={option}>
                                {option.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                    <Button
                        icon={<Save className="w-4 h-4" />}
                        onClick={handleSave}
                        disabled={isSaving || !selectedOnboardingProgress}
                        variant="primary"
                    >
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </Button>

                    {selectedOnboardingProgress && (
                        <div className="text-sm text-gray-600">
                            <p>
                                Will set: <span className="font-semibold">hasFinishedOnboarding = {
                                    selectedOnboardingProgress === OnboardingProgress.FinishedOnboarding ? 'true' : 'false'
                                }</span>
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Feature Flags Section */}
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Feature Flags</h2>

                {featureFlagsError && <ErrorDisplay message={featureFlagsError} />}

                <div className="flex items-center gap-4">
                    <Toggle checked={voiceModeEnabled} onChange={handleToggleVoiceMode} />
                    <div>
                        <p className="text-sm font-medium text-gray-700">Voice Mode</p>
                        <p className="text-xs text-gray-500">
                            Shows the voice conversation button in chat. Off for everyone by default.
                            The user also has to be Pro, and it never appears in journey quest chats.
                        </p>
                    </div>
                </div>
            </div>

            {/* STT Usage Section */}
            {currentUser.sttUsage && (
                <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">STT Usage</h2>

                    {sttError && <ErrorDisplay message={sttError} />}
                    {sttSuccessMessage && (
                        <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
                            {sttSuccessMessage}
                        </div>
                    )}

                    <div className="text-sm text-gray-800 space-y-4">
                        <div>
                            <label htmlFor="stt-sessions" className="block text-sm font-medium text-gray-700 mb-2">
                                Sessions This Week
                            </label>
                            <div className="flex items-center gap-2">
                                <input
                                    id="stt-sessions"
                                    type="number"
                                    min={0}
                                    value={sttSessionsThisWeek}
                                    onChange={(e) => setSttSessionsThisWeek(Number(e.target.value))}
                                    className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                                <Button
                                    icon={<RotateCcw className="w-4 h-4" />}
                                    onClick={handleResetSttSessions}
                                    disabled={isSavingStt}
                                    variant="secondary"
                                >
                                    Reset
                                </Button>
                            </div>
                        </div>

                        <p>
                            Weekly Reset Timestamp: <span className="font-bold">{formatDate(currentUser.sttUsage.weeklyResetTimestamp)}</span>
                        </p>

                        <Button
                            icon={<Save className="w-4 h-4" />}
                            onClick={handleSaveSttUsage}
                            disabled={isSavingStt}
                            variant="primary"
                        >
                            {isSavingStt ? 'Saving...' : 'Save STT Usage'}
                        </Button>
                    </div>
                </div>
            )}

            {/* Voice Usage Section */}
            {currentUser.voiceUsage && (
                <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Voice Usage</h2>

                    {voiceError && <ErrorDisplay message={voiceError} />}
                    {voiceSuccessMessage && (
                        <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
                            {voiceSuccessMessage}
                        </div>
                    )}

                    <div className="text-sm text-gray-800 space-y-4">
                        <div>
                            <label htmlFor="voice-conversations" className="block text-sm font-medium text-gray-700 mb-2">
                                Conversations This Week
                            </label>
                            <div className="flex items-center gap-2">
                                <input
                                    id="voice-conversations"
                                    type="number"
                                    min={0}
                                    value={voiceConversationsThisWeek}
                                    onChange={(e) => setVoiceConversationsThisWeek(Number(e.target.value))}
                                    className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                                <Button
                                    icon={<RotateCcw className="w-4 h-4" />}
                                    onClick={() => handleResetVoiceUsage('conversations')}
                                    disabled={isSavingVoice}
                                    variant="secondary"
                                >
                                    Reset
                                </Button>
                            </div>
                        </div>

                        <div>
                            <label htmlFor="voice-minutes" className="block text-sm font-medium text-gray-700 mb-2">
                                Minutes This Week
                            </label>
                            <div className="flex items-center gap-2">
                                <input
                                    id="voice-minutes"
                                    type="number"
                                    min={0}
                                    step="0.1"
                                    value={voiceMinutesThisWeek}
                                    onChange={(e) => setVoiceMinutesThisWeek(Number(e.target.value))}
                                    className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                                <Button
                                    icon={<RotateCcw className="w-4 h-4" />}
                                    onClick={() => handleResetVoiceUsage('minutes')}
                                    disabled={isSavingVoice}
                                    variant="secondary"
                                >
                                    Reset
                                </Button>
                            </div>
                        </div>

                        <p>
                            Weekly Reset Timestamp: <span className="font-bold">{formatDate(currentUser.voiceUsage.weeklyResetTimestamp)}</span>
                        </p>

                        <Button
                            icon={<Save className="w-4 h-4" />}
                            onClick={handleSaveVoiceUsage}
                            disabled={isSavingVoice}
                            variant="primary"
                        >
                            {isSavingVoice ? 'Saving...' : 'Save Voice Usage'}
                        </Button>
                    </div>
                </div>
            )}

            {/* OCD Cycle Steps Section */}
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                    <h2 className="text-xl font-semibold text-gray-900">OCD Cycle Steps</h2>
                    {isLoadingCycleSteps || isRegeneratingCycleSteps ? (
                        <Button
                            disabled
                            variant="primary"
                            size="sm"
                            icon={<RefreshCw className={`w-4 h-4 ${isRegeneratingCycleSteps ? 'animate-spin' : ''}`} />}
                        >
                            {isRegeneratingCycleSteps ? 'Regenerating...' : 'Regenerate'}
                        </Button>
                    ) : (
                        <ButtonWithConfirmation
                            onClick={handleRegenerateCycleSteps}
                            firstText="Regenerate"
                            secondText="Confirm Regenerate"
                            firstIcon={<RefreshCw className="w-4 h-4" />}
                            secondIcon={<RefreshCw className="w-4 h-4" />}
                            firstVariant="primary"
                            secondVariant="danger"
                            size="sm"
                        />
                    )}
                </div>

                {cycleStepsError && <ErrorDisplay message={cycleStepsError} />}
                {cycleStepsSuccessMessage && (
                    <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
                        {cycleStepsSuccessMessage}
                    </div>
                )}

                {isLoadingCycleSteps ? (
                    <p className="text-sm text-gray-600">Loading OCD cycle steps...</p>
                ) : isRegeneratingCycleSteps ? (
                    <p className="text-sm text-gray-600">Regenerating OCD cycle steps. This may take a minute...</p>
                ) : ocdCycleSteps ? (
                    <div className="space-y-4">
                        <div className="text-sm text-gray-800 space-y-1">
                            <p>
                                Created At: <span className="font-bold">{formatDate(ocdCycleSteps.createdAt)}</span>
                            </p>
                            <p>
                                Updated At: <span className="font-bold">{formatDate(ocdCycleSteps.updatedAt)}</span>
                            </p>
                            <p>
                                Step Count: <span className="font-bold">{ocdCycleSteps.steps.length}</span>
                            </p>
                        </div>

                        <div className="space-y-3">
                            {ocdCycleSteps.steps
                                .slice()
                                .sort((a, b) => a.cycleStep - b.cycleStep)
                                .map((step) => (
                                    <div
                                        key={step.cycleStep}
                                        className="border border-gray-200 rounded-md p-3 bg-gray-50"
                                    >
                                        <p className="text-xs font-semibold text-gray-500 mb-1">
                                            Step {step.cycleStep}
                                        </p>
                                        <p className="text-sm text-gray-800">{step.ollieText}</p>
                                    </div>
                                ))}
                        </div>
                    </div>
                ) : (
                    <p className="text-sm text-gray-600">No OCD cycle steps generated yet.</p>
                )}
            </div>
        </div>
    );
};
